/**
 * The DOM half of theming. `@communicator/shared`'s theme.ts owns the
 * vocabulary and the pure choice-to-theme decision; everything here touches a
 * document, a media query, or React.
 *
 * The applied theme is a data-theme attribute on <html>, matching the
 * selectors in src/styles/tokens.css. One attribute, four values, no class
 * juggling and no per-component logic: the token layer does the rest.
 */
import { useEffect } from 'react'
import {
  THEME_CHOICE_DEFAULT,
  isThemeChoice,
  resolveTheme,
  type AppliedTheme,
  type ThemeChoice,
} from '@communicator/shared'
import { useStore } from '../store'
import { writeCachedTheme } from './theme-cache'

const DARK_QUERY = '(prefers-color-scheme: dark)'

/**
 * Whether the operating system asks for dark. Guarded because matchMedia is
 * absent in some embedded web views and in jsdom builds; the fallback is dark
 * because dark is what this console has always defaulted to, so an
 * unanswerable question lands on the familiar answer rather than a flash of
 * white.
 */
export function prefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  return window.matchMedia(DARK_QUERY).matches
}

/**
 * Point `<meta name="theme-color">` at the page background of whatever theme
 * is now applied, so the browser's own chrome — the address-bar tint on
 * Android, the status-bar area of an installed console — stops being pinned
 * to a single dark value while the page underneath is light.
 *
 * Read from the cascade rather than from a table: the value has exactly one
 * home, `tokens.css`, and duplicating four page backgrounds into TypeScript
 * would both break the no-literals rule and guarantee drift the first time a
 * theme is retuned. Bails on an empty or unresolved answer — jsdom returns a
 * custom property's declared text, so `var(...)` here means "no real value",
 * never something worth writing into the document.
 *
 * NOT the same thing as the manifest's `background_color`: that one is read
 * by the OS at cold launch, before this or any other script exists, and is a
 * single static value for everyone. See public/manifest.webmanifest.
 */
function syncThemeColorMeta(): void {
  if (typeof document === 'undefined' || typeof window.getComputedStyle !== 'function') return
  const meta = document.querySelector('meta[name="theme-color"]')
  if (!meta) return
  const value = window.getComputedStyle(document.documentElement).getPropertyValue('--surface-page').trim()
  if (value.length === 0 || value.includes('var(')) return
  meta.setAttribute('content', value)
}

/**
 * Paint a choice. Returns what was actually applied, which differs from the
 * choice for Auto. Also caches the resolved id to localStorage (best-effort
 * — see ./theme-cache.ts) so the NEXT load can paint it before React mounts,
 * instead of flashing Legacy Night while prefs are still loading.
 */
export function applyTheme(choice: ThemeChoice): AppliedTheme {
  const applied = resolveTheme(choice, prefersDark())
  if (typeof document !== 'undefined') document.documentElement.dataset.theme = applied
  writeCachedTheme(applied)
  syncThemeColorMeta()
  return applied
}

/**
 * Re-apply whenever the OS flips between light and dark. `getChoice` is read
 * at event time, not captured, so the watch keeps working after someone
 * changes their preference without needing to be torn down and restarted.
 *
 * Harmless when the choice is explicit: resolveTheme ignores prefersDark for
 * everything but the two Auto entries, so the attribute is rewritten with the
 * value it already had.
 */
export function startThemeWatch(getChoice: () => ThemeChoice): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}
  const mql = window.matchMedia(DARK_QUERY)
  if (typeof mql.addEventListener !== 'function') return () => {}
  const onChange = () => {
    applyTheme(getChoice())
  }
  mql.addEventListener('change', onChange)
  return () => mql.removeEventListener('change', onChange)
}

/**
 * Keep the painted theme in step with this person's stored preference, and
 * with the OS while that preference is an Auto one.
 *
 * `prefs['p.theme']` is the single source of truth: it is what the server
 * stores and what the Settings select writes, so applying from it means the
 * picker needs no special case of its own. A value that is not a known choice
 * — an older client, a hand-edited row — falls back to the default rather
 * than painting nothing.
 *
 * Mounted once, in App, for the app's lifetime.
 */
export function useThemeSync(): void {
  const loaded = useStore((s) => s.prefsLoaded)
  const stored = useStore((s) => s.prefs['p.theme'])
  const setTheme = useStore((s) => s.setTheme)

  useEffect(() => {
    // WAIT FOR A REAL ANSWER. This guard is the whole fix for a flash that
    // survived two earlier attempts, because it was never a first-paint
    // problem: the inline <head> script painted the remembered theme
    // correctly, and then this effect ran on mount — with `prefs` still the
    // empty object it starts as — read `undefined`, concluded "no preference
    // set", and painted Legacy Night over it. Preferences landed a couple of
    // hundred milliseconds later and it flipped back. Every load, in every
    // tab, dark then right.
    //
    // It also wrote `legacy-night` into the localStorage cache on the way
    // past, so the cache spent most of its life holding the wrong answer.
    //
    // Doing nothing until the server has actually answered is correct in the
    // failure case too: with no answer, whatever the inline script restored
    // stands, which is a better guess than the default and the only one that
    // does not visibly change under the reader.
    if (!loaded) return
    setTheme(isThemeChoice(stored) ? stored : THEME_CHOICE_DEFAULT)
  }, [loaded, stored, setTheme])

  useEffect(() => startThemeWatch(() => useStore.getState().theme), [])
}
